import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InvoicesRoutes } from '../../../Router/path';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import Status from '../../Shared/Components/Status/Status';
import SaveButtons from '../../Shared/Components/SaveButtons/SaveButtons';
import {
  useCreateInvoiceCommandMutation,
  useGetNextInvoiceNumberQuery,
  useGetSellerCompanyIdByReservationQuery,
  useLinkPrepaymentsMutation,
  useRegenerateInvoicePdfMutation,
} from '../Service/extendedInvoicesApi';
import { useGetInvoiceCompanyLookupByIdQuery } from '../Service/companiesLookupApi';
import type { CreateInvoiceForm } from '../Types/createInvoiceFormTypes';
import {
  InvoiceType,
  InvoiceScheme,
  InvoiceStatus,
  PaymentMethodType,
} from '../Types/invoicesTypes';
import {
  InvoiceBasicsSection,
  InvoiceDocumentSection,
  InvoiceContractorSection,
  InvoiceItemsSection,
  InvoiceSellerSection,
  InvoicePfronRefundSection,
  InvoicePrepaymentPaymentSection,
  PrepaymentsList,
} from '../Components';
import {
  buildInvoicePayload,
  DEFAULT_INVOICE_TEMPLATE_VARIANT,
} from '../utils/invoiceHelpers';
import { createInitialInvoiceForm } from '../utils/createInvoiceInitialState';
import { useReservationPrefill } from '../hooks/useReservationPrefill';
import { useCrossBorderVatSuggestion } from '../hooks/useCrossBorderVatSuggestion';
import { getFieldLabel } from '../utils/fieldLabels';
import { invoiceLogger } from '../utils/invoiceLogger';
import { extractApiErrorMessage, extractValidationErrors } from '../../../Utils/apiErrorUtils';
import { applyApiValidationErrorsToForm } from '../utils/invoiceApiErrorHandling';
import { buildValidationSummary } from '../utils/validationSummary';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createInvoiceSchema } from '../Validation/createInvoice.schema';

const PREPAYMENT_ALREADY_SETTLED_MESSAGE_FRAGMENT =
  'jest już rozliczona z tą fakturą końcową';

const isIdempotentPrepaymentAlreadySettledError = (error: unknown): boolean => {
  const validationErrors = extractValidationErrors(error);
  if (!validationErrors) {
    return false;
  }

  const messages = Object.values(validationErrors)
    .flat()
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.toLowerCase());

  return (
    messages.length > 0 &&
    messages.every((message) => message.includes(PREPAYMENT_ALREADY_SETTLED_MESSAGE_FRAGMENT))
  );
};

const CreateInvoice: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialReservationId = useMemo(() => {
    const reservationIdParam = searchParams.get('reservationId');
    if (!reservationIdParam) {
      return undefined;
    }

    const parsedReservationId = parseInt(reservationIdParam, 10);
    return Number.isNaN(parsedReservationId) || parsedReservationId <= 0
      ? undefined
      : parsedReservationId;
  }, [searchParams]);
  const returnTo = searchParams.get('returnTo');

  React.useEffect(() => {
    if (typeof window.scrollTo !== 'function') {
      return;
    }

    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      try {
        window.scrollTo(0, 0);
      } catch {
        // JSDOM may expose scrollTo without implementing it.
      }
    }
  }, [initialReservationId, returnTo]);

  // Lokalny state dla wyboru szablonu PDF
  const [templateVariant, setTemplateVariant] = useState<string>(DEFAULT_INVOICE_TEMPLATE_VARIANT);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [submitOutcome, setSubmitOutcome] = useState<'idle' | 'success' | 'error' | 'partial'>(
    'idle'
  );
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
  const [isCreateResultAmbiguous, setIsCreateResultAmbiguous] = useState(false);
  const submitLockRef = React.useRef(false);

  const safeReturnTo = useMemo(() => {
    if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
      return null;
    }

    return returnTo;
  }, [returnTo]);

  const buildEditInvoiceRoute = useCallback(
    (invoiceId: number) => {
      const baseRoute = InvoicesRoutes.update.replace(':invoiceId', String(invoiceId));
      if (!safeReturnTo) {
        return baseRoute;
      }

      const params = new URLSearchParams({ returnTo: safeReturnTo });
      return `${baseRoute}?${params.toString()}`;
    },
    [safeReturnTo]
  );

  // RHF — wszystkie sekcje na react-hook-form
  const {
    control,
    handleSubmit: rhfHandleSubmit,
    setValue,
    resetField,
    getFieldState,
    watch,
    getValues,
    setError,
    clearErrors,
    trigger, // ✅ CRITICAL: Manual validation trigger
    formState: { errors, isValid },
  } = useForm<CreateInvoiceForm>({
    defaultValues: {
      ...createInitialInvoiceForm(),
      reservationId: initialReservationId,
    },
    resolver: yupResolver(createInvoiceSchema) as any,
    mode: 'onChange', // ✅ CRITICAL: Enable validation on change to track isValid
  });
  const validationSummary = useMemo(() => buildValidationSummary(errors), [errors]);

  // Cross-border VAT auto-suggestion hook
  const vatSuggestion = useCrossBorderVatSuggestion({
    setValue,
    getValues,
    autoApply: true, // Auto-apply VatEU when EU VAT number detected
  });

  // checkAndApply is now memoized inside the hook, can be used directly
  // No need for additional useCallback wrapper

  // Pola sterowane przez RHF (dla zapytań i prefill)
  const watchType = watch('type');
  const watchScheme = watch('scheme');
  const watchReservationId = watch('reservationId');
  const watchCompanyId = watch('companyId'); // NEW: Watch manual company selection
  const watchPaymentMethod = watch('paymentMethod'); // NEW: Watch payment method for auto-paid status
  const watchUseReservationPaymentImport = watch('useReservationPaymentImport');
  const watchIssueDate = watch('issueDate'); // NEW: Watch issue date for invoice number refetch
  const _watchSelectedPrepaymentIds = watch('selectedPrepaymentIds');
  const watchSelectedPrepaymentIds = useMemo(
    () => _watchSelectedPrepaymentIds || [],
    [_watchSelectedPrepaymentIds]
  );
  const watchRefundParticipantReferralCost = watch('refundParticipantReferralCost');
  const watchRefundGuardianReferralCost = watch('refundGuardianReferralCost');
  const watchRefundPfronSubsidyParticipant = watch('refundPfronSubsidyParticipant');
  const watchRefundPfronSubsidyGuardian = watch('refundPfronSubsidyGuardian');
  const watchRefundOwnContribution = watch('refundOwnContribution');

  const hasAnyRefundFieldsFilled = [
    watchRefundParticipantReferralCost,
    watchRefundGuardianReferralCost,
    watchRefundPfronSubsidyParticipant,
    watchRefundPfronSubsidyGuardian,
    watchRefundOwnContribution,
  ].some((value) => value !== undefined && value !== null);

  const shouldShowPfronRefundSection = watchType === InvoiceType.Refund || hasAnyRefundFieldsFilled;

  // Debug: sprawdź wartości type i scheme
  React.useEffect(() => {
    invoiceLogger.debug('[CreateInvoice] watchType:', watchType, 'typeof:', typeof watchType);
    invoiceLogger.debug('[CreateInvoice] watchScheme:', watchScheme, 'typeof:', typeof watchScheme);
    invoiceLogger.debug('[CreateInvoice] isPrepayment:', watchType === InvoiceType.Prepayment);
    invoiceLogger.debug('[CreateInvoice] isFinal:', watchType === InvoiceType.Final);
    invoiceLogger.debug('[CreateInvoice] isStandard:', watchScheme === InvoiceScheme.Standard);
  }, [watchType, watchScheme]);

  const [createInvoice, createStatus] = useCreateInvoiceCommandMutation();
  const [linkPrepayments] = useLinkPrepaymentsMutation();
  const [regenerateInvoicePdf] = useRegenerateInvoicePdfMutation();

  // pobierz pełną rezerwację -> uzupełnij nabywcę i saleDate przez RHF
  const { reservationDetails } = useReservationPrefill(
    watchReservationId,
    setValue,
    getFieldState,
    resetField,
    watchUseReservationPaymentImport
  );

  const reservationPaymentSummary = reservationDetails?.paymentSummary;
  const hasImportableReservationPayment = (reservationPaymentSummary?.importablePaidAmount ?? 0) > 0;
  const previousReservationIdRef = React.useRef<number | undefined>(undefined);

  const reservationPaymentMethodLabel = useMemo(() => {
    if (!reservationPaymentSummary) {
      return 'Przelew';
    }

    if (reservationPaymentSummary.isMixedPayment) {
      return 'Mieszana (gotówka + przelew)';
    }

    return reservationPaymentSummary.suggestedPaymentMethod === PaymentMethodType.Cash
      ? 'Gotówka'
      : 'Przelew';
  }, [reservationPaymentSummary]);

  const reservationPaidAtLabel = useMemo(() => {
    const raw = reservationPaymentSummary?.suggestedPaidAt;
    return raw && raw.length >= 10 ? raw.slice(0, 10) : '-';
  }, [reservationPaymentSummary]);

  const sanitizeDisabledReservationPaymentImport = useCallback(
    (formData: CreateInvoiceForm): CreateInvoiceForm => {
      if (
        !formData.reservationId ||
        formData.useReservationPaymentImport !== false ||
        formData.type === InvoiceType.Prepayment
      ) {
        return formData;
      }

      const sanitizedFormData = { ...formData };

      if (!getFieldState('paidAmount').isDirty) {
        sanitizedFormData.paidAmount = undefined;
      }

      if (!getFieldState('paidAt').isDirty) {
        sanitizedFormData.paidAt = undefined;
      }

      if (!getFieldState('paymentComment').isDirty) {
        sanitizedFormData.paymentComment = undefined;
      }

      if (!getFieldState('paymentMethod').isDirty) {
        sanitizedFormData.paymentMethod = PaymentMethodType.Transfer;
      }

      if (!getFieldState('status').isDirty && sanitizedFormData.status === InvoiceStatus.Paid) {
        sanitizedFormData.status = undefined;
      }

      return sanitizedFormData;
    },
    [getFieldState]
  );

  const handlePaymentImportToggle = useCallback(
    (checked: boolean) => {
      setValue('useReservationPaymentImport', checked, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: false,
      });

      if (checked) {
        return;
      }

      // Keep explicit user input (dirty), clear auto-imported values otherwise.
      const paidAmountState = getFieldState('paidAmount');
      if (!paidAmountState.isDirty) {
        resetField('paidAmount', { defaultValue: undefined });
      }

      const paidAtState = getFieldState('paidAt');
      if (!paidAtState.isDirty) {
        resetField('paidAt', { defaultValue: undefined });
      }

      const paymentCommentState = getFieldState('paymentComment');
      if (!paymentCommentState.isDirty) {
        resetField('paymentComment', { defaultValue: undefined });
      }

      const paymentMethodState = getFieldState('paymentMethod');
      if (!paymentMethodState.isDirty) {
        resetField('paymentMethod', { defaultValue: PaymentMethodType.Transfer });
      }
    },
    [getFieldState, resetField, setValue]
  );

  // Reset selected prepayments when reservation changes
  React.useEffect(() => {
    const previousReservationId = previousReservationIdRef.current;
    if (previousReservationId === undefined) {
      previousReservationIdRef.current = watchReservationId;
      return;
    }

    if (previousReservationId !== watchReservationId) {
      setValue('selectedPrepaymentIds', []);
    }

    previousReservationIdRef.current = watchReservationId;
  }, [watchReservationId, setValue]);

  // Auto-fill payment fields when type changes to Prepayment
  React.useEffect(() => {
    if (watchType === InvoiceType.Prepayment) {
      setValue('status', InvoiceStatus.Paid);
      if (!watch('paidAt')) {
        setValue('paidAt', new Date().toISOString().slice(0, 10));
      }
      // ✅ Auto-fill default payment comment for Prepayment
      if (!watch('paymentComment')) {
        setValue('paymentComment', 'Zaliczka za wyjazd');
        invoiceLogger.debug('[CreateInvoice] Auto-setting paymentComment for Prepayment');
      }
    }
  }, [watchType, setValue, watch]);

  // ✅ NEW: Auto-mark as Paid for cash payments (except Prepayment - already handled above)
  // Backend: PaymentStatusHelper.ApplyPaymentStatus() does the same server-side
  React.useEffect(() => {
    const isCashPayment = watchPaymentMethod === PaymentMethodType.Cash;
    const isPrepayment = watchType === InvoiceType.Prepayment;

    // Only auto-set for cash if NOT Prepayment (Prepayment handled by separate effect)
    if (isCashPayment && !isPrepayment) {
      invoiceLogger.debug('[CreateInvoice] Cash payment detected, auto-setting status to Paid');
      setValue('status', InvoiceStatus.Paid);

      // Auto-fill paidAt with issueDate if not already set
      const currentPaidAt = watch('paidAt');
      const issueDate = watch('issueDate');
      if (!currentPaidAt && issueDate) {
        setValue('paidAt', issueDate);
        invoiceLogger.debug('[CreateInvoice] Auto-setting paidAt to issueDate:', issueDate);
      }

      // ✅ Auto-fill default payment comment for cash (backend will override if needed)
      if (!watch('paymentComment')) {
        const comment = 'Zapłacono gotówką';
        setValue('paymentComment', comment);
        invoiceLogger.debug('[CreateInvoice] Auto-setting paymentComment:', comment);
      }
    }
  }, [watchPaymentMethod, watchType, setValue, watch]);

  // AUTOFILL SELLER
  const {
    data: sellerCompanyId,
    isError: isSellerCompanyIdError,
    error: sellerCompanyIdError,
  } = useGetSellerCompanyIdByReservationQuery(watchReservationId!, {
    skip: !watchReservationId,
  });

  React.useEffect(() => {
    if (!watchReservationId) {
      return;
    }

    if (isSellerCompanyIdError) {
      invoiceLogger.error('[CreateInvoice] Failed to resolve seller company id from reservation', {
        reservationId: watchReservationId,
        sellerCompanyIdError,
      });
      return;
    }

    if (sellerCompanyId) {
      invoiceLogger.debug('[CreateInvoice] Resolved seller company id from reservation', {
        reservationId: watchReservationId,
        sellerCompanyId,
      });
    }
  }, [watchReservationId, isSellerCompanyIdError, sellerCompanyIdError, sellerCompanyId]);
  const { data: sellerCompany } = useGetInvoiceCompanyLookupByIdQuery(sellerCompanyId!, {
    skip: !sellerCompanyId,
  });

  // NEW: Fetch manually selected company (for invoices without reservation)
  const { data: manualCompany } = useGetInvoiceCompanyLookupByIdQuery(watchCompanyId!, {
    skip: !watchCompanyId || !!watchReservationId, // Skip if reservation exists (use sellerCompany instead)
  });

  // Next invoice number prefill (requires companyId from reservation or manual selection)
  const {
    data: nextNumber,
    isLoading: isNextLoading,
    isError: isNextNumberError,
    error: nextNumberError,
  } = useGetNextInvoiceNumberQuery(
    {
      type: watchType,
      companyId: watchCompanyId || sellerCompanyId,
      issueDate: watchIssueDate, // NEW: For date-based numbering (e.g., monthly counters)
    },
    { skip: !watchType || (!watchCompanyId && !sellerCompanyId) }
  );

  React.useEffect(() => {
    if (!isNextNumberError) {
      return;
    }

    invoiceLogger.error('[CreateInvoice] Failed to fetch next invoice number preview', {
      type: watchType,
      companyId: watchCompanyId || sellerCompanyId,
      issueDate: watchIssueDate,
      error: nextNumberError,
    });
  }, [
    isNextNumberError,
    nextNumberError,
    sellerCompanyId,
    watchCompanyId,
    watchIssueDate,
    watchType,
  ]);

  // Auto-resolve companyId from reservation (for invoice numbering per company)
  React.useEffect(() => {
    if (sellerCompanyId) {
      invoiceLogger.debug(
        '[CreateInvoice] Auto-setting companyId from reservation:',
        sellerCompanyId
      );
      setValue('companyId', sellerCompanyId, { shouldValidate: true, shouldDirty: true });
    } else if (watchReservationId && isSellerCompanyIdError) {
      invoiceLogger.warn(
        '[CreateInvoice] Seller company not resolved for reservation, switching to manual company selection',
        {
          reservationId: watchReservationId,
          sellerCompanyIdError,
        }
      );
      setValue('companyId', undefined, { shouldValidate: true, shouldDirty: true });
    } else if (!watchReservationId) {
      // Clear companyId when reservation is removed
      invoiceLogger.debug('[CreateInvoice] Clearing companyId (no reservation)');
      setValue('companyId', undefined, { shouldValidate: false });
    }
  }, [sellerCompanyId, watchReservationId, isSellerCompanyIdError, sellerCompanyIdError, setValue]);

  // Auto-fill seller data from company (reservation or manual selection)
  React.useEffect(() => {
    // Use sellerCompany for reservation invoices, manualCompany for manual invoices
    const company = sellerCompany || manualCompany;
    if (!company) return;

    invoiceLogger.debug('[CreateInvoice] Auto-filling seller data from company:', company);

    const name = company.name;
    const streetNo = company.fullStreetAddress || company.street;
    const rest = company.cityWithPostalCode || company.city;
    const nip = company.nip;

    const canAutofillSellerField = (
      fieldName: 'sellerName' | 'sellerStreetNo' | 'sellerAddress' | 'sellerNip'
    ) => {
      const fieldState = getFieldState(fieldName);
      return !fieldState.isDirty && !fieldState.isTouched;
    };

    const setSellerField = (
      fieldName: 'sellerName' | 'sellerStreetNo' | 'sellerAddress' | 'sellerNip',
      value?: string
    ) => {
      if (!value || !canAutofillSellerField(fieldName)) {
        return;
      }

      setValue(fieldName, value, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    };

    // Autofill seller fields only while they are still pristine.
    setSellerField('sellerName', name);
    setSellerField('sellerStreetNo', streetNo);
    setSellerField('sellerAddress', rest);
    setSellerField('sellerNip', nip);
  }, [getFieldState, manualCompany, sellerCompany, setValue]);

  // Submit: wszystkie dane z RHF
  const onSubmit = useCallback(
    async (
      rhfFormData: CreateInvoiceForm
    ): Promise<{ createdInvoiceId: number; partialWarning?: string }> => {
      let hasPartialFailure = false;

      setIsSubmitInProgress(true);
      setSubmitErrorMessage(null);
      setSubmitOutcome('idle');
      createStatus.reset?.();
      clearErrors();

      invoiceLogger.debug('═══════════════════════════════════════════════════════════════');
      invoiceLogger.debug('🚀 [CreateInvoice] SUBMIT CLICKED - Starting invoice creation');
      invoiceLogger.debug('═══════════════════════════════════════════════════════════════');

      invoiceLogger.debug('[CreateInvoice] 📋 Raw form data from React Hook Form:', {
        type: rhfFormData.type,
        scheme: rhfFormData.scheme,
        reservationId: rhfFormData.reservationId,
        companyId: rhfFormData.companyId,
        status: rhfFormData.status,
        paidAmount: rhfFormData.paidAmount,
        paidAt: rhfFormData.paidAt,
        items: rhfFormData.items?.map((item, idx) => ({
          index: idx,
          name: item.name,
          unitNet: item.unitNet,
          vatType: item.vatType,
          quantity: item.quantity,
        })),
      });

      const sanitizedFormData = sanitizeDisabledReservationPaymentImport(rhfFormData);

      // Dodaj templateVariant do formData przed budowaniem payloadu
      const formDataWithTemplate = { ...sanitizedFormData, templateVariant };
      const payload = buildInvoicePayload(formDataWithTemplate);

      invoiceLogger.debug('─────────────────────────────────────────────────────────────');
      invoiceLogger.debug('[CreateInvoice] 📦 Built payload (after buildInvoicePayload):');
      invoiceLogger.debug(JSON.stringify(payload, null, 2));
      invoiceLogger.debug('─────────────────────────────────────────────────────────────');

      try {
        invoiceLogger.debug('[CreateInvoice] 🌐 Sending POST request to backend...');
        const result = await createInvoice(payload).unwrap();
        const legacyResult = result as { id?: unknown };
        const legacyInvoiceId = typeof legacyResult.id === 'number' ? legacyResult.id : undefined;
        // Backward compat: backend may return { invoiceId } (new) or { id } (old)
        const createdInvoiceId = result.invoiceId ?? legacyInvoiceId;
        if (
          typeof createdInvoiceId !== 'number' ||
          !Number.isInteger(createdInvoiceId) ||
          createdInvoiceId <= 0
        ) {
          const missingInvoiceIdError = new Error(
            'CreateInvoice response missing invoice identifier'
          ) as Error & { isMissingInvoiceId?: boolean };
          missingInvoiceIdError.isMissingInvoiceId = true;
          setIsCreateResultAmbiguous(true);
          setSubmitOutcome('error');
          setSubmitErrorMessage(
            'Nie udało się ustalić identyfikatora nowo utworzonej faktury. Otwórz listę faktur i sprawdź, czy dokument został zapisany.'
          );
          throw missingInvoiceIdError;
        }

        invoiceLogger.debug('═══════════════════════════════════════════════════════════════');
        invoiceLogger.debug('[CreateInvoice] ✅ Backend response SUCCESS:');
        invoiceLogger.debug(JSON.stringify(result, null, 2));
        invoiceLogger.debug('═══════════════════════════════════════════════════════════════');

        // Link prepayments if Final invoice with selected prepayments
        if (
          rhfFormData.type === InvoiceType.Final &&
          rhfFormData.selectedPrepaymentIds &&
          rhfFormData.selectedPrepaymentIds.length > 0
        ) {
          invoiceLogger.debug(
            '[CreateInvoice] 🔗 Linking prepayments:',
            rhfFormData.selectedPrepaymentIds
          );
          try {
            await linkPrepayments({
              finalInvoiceId: createdInvoiceId,
              prepaymentIds: rhfFormData.selectedPrepaymentIds,
            }).unwrap();
            invoiceLogger.debug('[CreateInvoice] ✅ Prepayments linked successfully');

            await regenerateInvoicePdf({
              id: createdInvoiceId,
              templateVariant,
            }).unwrap();
            invoiceLogger.debug('[CreateInvoice] ✅ PDF regenerated after prepayments linking');
          } catch (linkError: any) {
            const isIdempotentAlreadySettledConflict =
              isIdempotentPrepaymentAlreadySettledError(linkError);

            if (isIdempotentAlreadySettledConflict) {
              invoiceLogger.warn(
                '[CreateInvoice] ℹ️ Prepayments already settled with current final invoice - treating link as idempotent success.'
              );

              try {
                await regenerateInvoicePdf({
                  id: createdInvoiceId,
                  templateVariant,
                }).unwrap();
                invoiceLogger.debug(
                  '[CreateInvoice] ✅ PDF regenerated after idempotent prepayment link conflict'
                );
                setSubmitOutcome('success');
              } catch (regenerateError: any) {
                const regenerateMessage = extractApiErrorMessage(
                  regenerateError,
                  'Nie udało się odświeżyć PDF po zapisie faktury końcowej.'
                );
                const warningMessage = `Faktura została zapisana i zaliczki były już powiązane, ale ${regenerateMessage} Otwórz widok edycji i ponów generowanie PDF.`;

                hasPartialFailure = true;
                setSubmitOutcome('partial');
                setSubmitErrorMessage(warningMessage);

                const partialSuccessError = (
                  regenerateError instanceof Error
                    ? regenerateError
                    : new Error('PDF regeneration failed after idempotent prepayment link conflict')
                ) as Error & {
                  createdInvoiceId?: number;
                  isPartialSuccess?: boolean;
                  partialWarning?: string;
                };
                partialSuccessError.createdInvoiceId = createdInvoiceId;
                partialSuccessError.isPartialSuccess = true;
                partialSuccessError.partialWarning = warningMessage;
                throw partialSuccessError;
              }

              // Idempotent conflict handled as success - do not surface partial warning.
              return { createdInvoiceId };
            }

            const linkMessage = extractApiErrorMessage(
              linkError,
              'Nie udało się zaktualizować PDF po powiązaniu zaliczek z nowo utworzoną fakturą końcową.'
            );

            hasPartialFailure = true;
            setSubmitOutcome('partial');
            setSubmitErrorMessage(
              `${linkMessage} Otwórz widok edycji i ponów generowanie PDF.`
            );

            invoiceLogger.error('═══════════════════════════════════════════════════════════════');
            invoiceLogger.error(
              '[CreateInvoice] ⚠️ Invoice created but prepayment linking or PDF regeneration failed'
            );
            invoiceLogger.error('[CreateInvoice] ⚠️ Post-create error:', linkError);
            invoiceLogger.error('═══════════════════════════════════════════════════════════════');

            const partialSuccessError = (
              linkError instanceof Error
                ? linkError
                : new Error('Post-create step failed for created invoice')
            ) as Error & {
              createdInvoiceId?: number;
              isPartialSuccess?: boolean;
              partialWarning?: string;
            };
            partialSuccessError.createdInvoiceId = createdInvoiceId;
            partialSuccessError.isPartialSuccess = true;
            partialSuccessError.partialWarning = `${linkMessage} Otwórz widok edycji i ponów generowanie PDF.`;
            throw partialSuccessError;
          }
        }

        setSubmitOutcome('success');

        return { createdInvoiceId };
        } catch (err: any) {
        const isMissingInvoiceIdError = err?.isMissingInvoiceId === true;
        if (!hasPartialFailure && !isMissingInvoiceIdError) {
          applyApiValidationErrorsToForm(err, setError);

          const status = typeof err?.status === 'number' ? err.status : undefined;
          const fallbackMessage =
            status && status >= 500
              ? 'Błąd serwera podczas zapisu faktury. Spróbuj ponownie za chwilę lub skontaktuj się z administratorem.'
              : 'Nie udało się utworzyć faktury. Zweryfikuj dane i spróbuj ponownie.';
          const userMessage = extractApiErrorMessage(err, fallbackMessage);
          setSubmitOutcome('error');
          setSubmitErrorMessage(userMessage);
        }

        invoiceLogger.error('═══════════════════════════════════════════════════════════════');
        invoiceLogger.error('[CreateInvoice] ❌ Backend ERROR occurred');
        invoiceLogger.error('═══════════════════════════════════════════════════════════════');
        invoiceLogger.error('[CreateInvoice] ❌ Error object:', err);
        invoiceLogger.error('[CreateInvoice] ❌ Error type:', typeof err);
        invoiceLogger.error('[CreateInvoice] ❌ Error keys:', Object.keys(err || {}));

        if (err?.data) {
          invoiceLogger.error('[CreateInvoice] ❌ Error data:', JSON.stringify(err.data, null, 2));
        }

        if (err?.status) {
          invoiceLogger.error('[CreateInvoice] ❌ HTTP Status:', err.status);
        }

        if (err?.message) {
          invoiceLogger.error('[CreateInvoice] ❌ Error message:', err.message);
        }

        invoiceLogger.error(
          '[CreateInvoice] ❌ Full error (stringified):',
          JSON.stringify(err, null, 2)
        );
        invoiceLogger.error('═══════════════════════════════════════════════════════════════');

        throw err; // pozwól Status/SaveButtons zareagować
      } finally {
        setIsSubmitInProgress(false);
      }
    },
    [
      clearErrors,
      createInvoice,
      createStatus,
      linkPrepayments,
      regenerateInvoicePdf,
      sanitizeDisabledReservationPaymentImport,
      setError,
      templateVariant,
    ]
  );

  // Stable callback for prepayments selection
  const handlePrepaymentsSelectionChange = useCallback(
    (ids: number[]) => {
      setValue('selectedPrepaymentIds', ids);
    },
    [setValue]
  );

  return (
    <CustomCard title="Dodaj fakturę" icon={<ReceiptLongIcon />}>
      {/* Sekcja podstawowa — już na RHF */}
      <InvoiceBasicsSection
        control={control as any}
        errors={errors}
        setValue={setValue}
        nextNumber={nextNumber}
        isNextLoading={isNextLoading}
        isNextError={isNextNumberError}
        nextNumberErrorMessage={
          isNextNumberError
            ? 'Nie udało się pobrać numeru. Sprawdź typ faktury i firmę wystawiającą.'
            : undefined
        }
        sellerCompanyId={sellerCompanyId}
        isSellerCompanyIdError={isSellerCompanyIdError}
      />

      {watchReservationId && hasImportableReservationPayment && (
        <Box mt={3}>
          <CustomCard title="Import wpłat z rezerwacji">
            <Alert severity="info" sx={{ mb: 2 }}>
              Wykryto wpłaty na rezerwacji. Możesz użyć ich do wstępnego uzupełnienia pól płatności faktury.
            </Alert>

            <Typography variant="body2" sx={{ mb: 1 }}>
              Saldo importowalne po odjęciach:{' '}
              <strong>{(reservationPaymentSummary?.importablePaidAmount ?? 0).toFixed(2)} PLN</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Importujesz tylko saldo importowalne (po odjęciu kwot już rozliczonych).
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Metoda sugerowana: <strong>{reservationPaymentMethodLabel}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Data sugerowana: <strong>{reservationPaidAtLabel}</strong>
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={!!watchUseReservationPaymentImport}
                  onChange={(_, checked) => handlePaymentImportToggle(checked)}
                />
              }
              label="Użyj zaimportowanych wartości płatności z rezerwacji"
            />
          </CustomCard>
        </Box>
      )}

      {/* Sekcja płatności — tylko dla faktur Prepayment */}
      {watchType === InvoiceType.Prepayment && (
        <InvoicePrepaymentPaymentSection control={control as any} errors={errors} />
      )}

      {/* Sekcja dokumentu — migracja na RHF ✅ */}
      <InvoiceDocumentSection
        control={control as any}
        errors={errors}
        setValue={setValue}
      />

      {/* Wybór szablonu PDF */}
      <Box mt={3}>
        <CustomCard title="Szablon PDF">
          <FormControl fullWidth>
            <InputLabel>Szablon PDF</InputLabel>
            <Select
              value={templateVariant}
              onChange={(e) => setTemplateVariant(e.target.value)}
              label="Szablon PDF"
            >
              <MenuItem value="modern">Modern (Premium) - kolorowy</MenuItem>
            </Select>
          </FormControl>
        </CustomCard>
      </Box>

      {/* Sekcja sprzedawcy — migracja na RHF ✅ */}
      <InvoiceSellerSection control={control as any} errors={errors} />

      {/* Sekcja kontrahenta — migracja na RHF ✅ */}
      <InvoiceContractorSection
        control={control as any}
        errors={errors}
        onBuyerNipChange={vatSuggestion.checkAndApply}
      />

      <InvoiceItemsSection
        control={control as any}
        setValue={setValue}
        errors={errors}
        reservationId={watchReservationId}
        scheme={watchScheme}
        invoiceType={watchType}
        onItemAdded={vatSuggestion.checkAndApply}
      />

      {/* Sekcja Prepayments — dla wszystkich faktur końcowych powiązanych z rezerwacją */}
      {watchType === InvoiceType.Final && watchReservationId && (
        <Box mt={3}>
          <CustomCard title="Rozliczenie zaliczek">
            <PrepaymentsList
              reservationId={watchReservationId}
              selectedPrepaymentIds={watchSelectedPrepaymentIds}
              onSelectionChange={handlePrepaymentsSelectionChange}
              disabled={isSubmitInProgress}
            />
          </CustomCard>
        </Box>
      )}

      {/* Sekcja PFRON Refund — tylko dla faktur refundacyjnych */}
      {shouldShowPfronRefundSection && (
        <InvoicePfronRefundSection control={control as any} errors={errors} />
      )}

      {/* ✅ Error Summary - sticky alert at bottom when validation errors exist */}
      <Collapse in={validationSummary.length > 0}>
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon />}
          sx={{
            mt: 2,
            mb: 2,
            position: 'sticky',
            bottom: 80, // Above SaveButtons
            zIndex: 1000,
            boxShadow: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Formularz zawiera błędy walidacji ({validationSummary.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Proszę poprawić poniższe pola przed zapisaniem:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {validationSummary.map(({ field, message }) => (
              <li key={`${field}-${message ?? 'validation'}`}>
                <Typography variant="body2">
                  <strong>{getFieldLabel(field)}:</strong> {message || 'Błąd walidacji'}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      </Collapse>

      {submitErrorMessage && (
        <Alert severity={submitOutcome === 'partial' ? 'warning' : 'error'} sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            {submitOutcome === 'partial'
              ? 'Faktura została zapisana, ale nie udało się powiązać zaliczek'
              : 'Nie udało się zapisać faktury'}
          </Typography>
          <Typography variant="body2">{submitErrorMessage}</Typography>
          {isCreateResultAmbiguous && (
            <Box mt={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(InvoicesRoutes.root)}
              >
                Przejdź do listy faktur
              </Button>
            </Box>
          )}
        </Alert>
      )}

      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
        <Status
          isLoading={isSubmitInProgress}
          isError={false}
          isSuccess={submitOutcome === 'success'}
          error={undefined}
        />
        <SaveButtons
          isUpdating={isSubmitInProgress || isCreateResultAmbiguous}
          handleSubmit={async (_e, shouldRedirect) => {
            if (isCreateResultAmbiguous) {
              invoiceLogger.warn(
                '[CreateInvoice] ⛔ Submit blocked due to ambiguous create result (missing invoice id)'
              );
              return;
            }
            if (submitLockRef.current) {
              invoiceLogger.warn('[CreateInvoice] 🔒 Ignoring submit, another submit is in progress');
              return;
            }

            submitLockRef.current = true;
            try {
              invoiceLogger.debug('═══════════════════════════════════════════════════════════════');
              invoiceLogger.debug(
                '🖱️  [CreateInvoice] BUTTON CLICKED:',
                shouldRedirect ? 'ZAPISZ I WYJDŹ' : 'ZAPISZ I KONTYNUUJ'
              );
              invoiceLogger.debug('═══════════════════════════════════════════════════════════════');

              // ✅ CRITICAL: Manually trigger validation to ensure errors are up-to-date
              invoiceLogger.debug('[CreateInvoice] 🔍 Manually triggering validation...');
              const validationResult = await trigger();

              invoiceLogger.debug('[CreateInvoice] 📊 Validation result:', {
                validationPassed: validationResult,
                isValid,
                errorsCount: Object.keys(errors).length,
                errors: errors,
              });

              // ✅ DEBUG: Log detailed errors for debugging
              if (errors && Object.keys(errors).length > 0) {
                invoiceLogger.error('[CreateInvoice] 📋 Detailed validation errors:');
                Object.entries(errors).forEach(([field, error]) => {
                  invoiceLogger.error(`  - ${field}:`, error);
                });
              }

              // ✅ CRITICAL: Check ONLY validationResult (trigger returns false if ANY field is invalid)
              // DON'T rely on errors object count - it may not be updated yet due to React batching
              if (!validationResult) {
                invoiceLogger.error(
                  '═══════════════════════════════════════════════════════════════'
                );
                invoiceLogger.error('[CreateInvoice] ⛔ BLOCKING SUBMIT - Validation failed');
                invoiceLogger.error('[CreateInvoice] ⛔ validationResult:', validationResult);
                invoiceLogger.error(
                  '[CreateInvoice] ⛔ Current errors:',
                  JSON.stringify(errors, null, 2)
                );
                invoiceLogger.error(
                  '═══════════════════════════════════════════════════════════════'
                );

                // Force scroll to first error field
                setTimeout(() => {
                  const firstErrorField = document.querySelector('[aria-invalid="true"]');
                  if (firstErrorField) {
                    firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);

                return; // ✅ Exit early - don't call backend, don't redirect
              }

              // ✅ CRITICAL: Track validation success
              let submissionPassed = false;
              let partialSubmission = false;
              let createdInvoiceId: number | null = null;
              let partialCreatedInvoiceId: number | null = null;
              let partialWarningMessage: string | null = null;

              // RHF submit (validation already done above)
              try {
                invoiceLogger.debug(
                  '[CreateInvoice] Running submission (validation already passed)...'
                );
                await rhfHandleSubmit(async (formData) => {
                  const submissionResult = await onSubmit(formData);
                  createdInvoiceId = submissionResult.createdInvoiceId;
                })();

                invoiceLogger.debug('[CreateInvoice] ✅ Submission successful!');
                submissionPassed = true; // ✅ Mark submission as successful
              } catch (err: any) {
                partialSubmission = err?.isPartialSuccess === true;
                const maybeCreatedInvoiceId = err?.createdInvoiceId;
                if (
                  typeof maybeCreatedInvoiceId === 'number' &&
                  Number.isInteger(maybeCreatedInvoiceId) &&
                  maybeCreatedInvoiceId > 0
                ) {
                  partialCreatedInvoiceId = maybeCreatedInvoiceId;
                }
                if (typeof err?.partialWarning === 'string') {
                  partialWarningMessage = err.partialWarning;
                }
                invoiceLogger.error(
                  '═══════════════════════════════════════════════════════════════'
                );
                invoiceLogger.error('[CreateInvoice] ❌ Submit failed in handleSubmit wrapper');
                invoiceLogger.error('[CreateInvoice] ❌ Error:', err);
                invoiceLogger.error(
                  '═══════════════════════════════════════════════════════════════'
                );
                submissionPassed = false; // ❌ Submission failed
              }

              // ✅ CRITICAL: Only redirect if submission passed
              if (submissionPassed && shouldRedirect) {
                invoiceLogger.debug('[CreateInvoice] 🔙 Navigating after save', {
                  action: 'history-back',
                });
                navigate(-1); // powrót, gdy kliknięto "Zapisz zmiany"
              } else if (submissionPassed && !shouldRedirect) {
                if (createdInvoiceId) {
                  navigate(buildEditInvoiceRoute(createdInvoiceId), { replace: true });
                } else {
                  invoiceLogger.error(
                    '[CreateInvoice] ❌ Missing created invoice id after successful submit'
                  );
                }
              } else if (!shouldRedirect && partialSubmission && partialCreatedInvoiceId) {
                invoiceLogger.warn(
                  '[CreateInvoice] ⚠️  Navigating to edit after partial create success',
                  { partialCreatedInvoiceId }
                );
                navigate(buildEditInvoiceRoute(partialCreatedInvoiceId), {
                  replace: true,
                  state: partialWarningMessage
                    ? { partialWarning: partialWarningMessage }
                    : undefined,
                });
              } else {
                invoiceLogger.warn('[CreateInvoice] ⚠️  NOT redirecting - submission failed');
              }
            } finally {
              submitLockRef.current = false;
            }
          }}
        />
      </Box>
    </CustomCard>
  );
};

export default CreateInvoice;
